import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
  FileText,
  Calendar as CalendarIcon,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import PromoBannerFormDialog from "@/components/PromoBannerFormDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = iso.length <= 10 ? new Date(iso + "T00:00:00") : new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function bannerIsLive(b) {
  if (!b.active) return false;
  if (!b.expires_at) return true;
  try {
    // Normalize to the same UTC end-of-day rule the backend uses for
    // date-only strings, so client and server agree.
    const exp =
      b.expires_at.length <= 10
        ? new Date(b.expires_at + "T23:59:59Z")
        : new Date(b.expires_at);
    return exp.getTime() > Date.now();
  } catch {
    return true;
  }
}

/**
 * Admin-only section embedded in ManufacturerDetailPage. Lists, creates, edits,
 * and deletes promo banners for one manufacturer.
 */
export default function PromoBannersSection({ manufacturerId, canManage }) {
  const [banners, setBanners] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | bannerObj
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/promo-banners", {
        params: { manufacturer_id: manufacturerId },
      });
      setBanners(data || []);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load promo banners"));
      setBanners([]);
    }
  }, [manufacturerId]);

  useEffect(() => {
    if (!canManage) return;
    load();
  }, [canManage, load]);

  if (!canManage) return null;

  const onDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/promo-banners/${confirmDelete.id}`);
      toast.success("Promo banner deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not delete banner"));
    } finally {
      setDeleting(false);
    }
  };

  const flyerHref = (b) => {
    if (b.flyer_pdf_id) return `${BACKEND_URL}/api/pdfs/${b.flyer_pdf_id}`;
    if (b.flyer_url) return b.flyer_url;
    return null;
  };

  return (
    <div className="bg-white border border-border rounded-sm p-4" data-testid="promo-banners-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-amber-600" />
          <div>
            <h3 className="font-display text-sm font-semibold tracking-tight">
              Promo banners
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Shown on the search page after a user picks this manufacturer.
              Auto-hides when expired or toggled off.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setEditing("new")}
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          data-testid="promo-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add promo
        </Button>
      </div>

      {banners === null ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      ) : banners.length === 0 ? (
        <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-sm">
          No promo banners yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {banners.map((b) => {
            const live = bannerIsLive(b);
            const href = flyerHref(b);
            return (
              <li
                key={b.id}
                className="border border-border rounded-sm p-3 flex items-start gap-3"
                data-testid={`promo-row-${b.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-sm text-slate-900 truncate">
                      {b.title}
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wider font-mono-data px-1.5 py-0.5 rounded-sm border ${
                        live
                          ? "text-green-700 bg-green-50 border-green-200"
                          : "text-slate-500 bg-slate-50 border-slate-200"
                      }`}
                    >
                      {live ? "Live" : !b.active ? "Hidden" : "Expired"}
                    </span>
                  </div>
                  {b.description && (
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {b.description}
                    </div>
                  )}
                  <div className="flex items-center gap-3 flex-wrap mt-1.5 text-[11px] text-muted-foreground">
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-amber-700 hover:underline"
                        data-testid={`promo-flyer-link-${b.id}`}
                      >
                        {b.flyer_pdf_id ? (
                          <FileText className="w-3 h-3" />
                        ) : (
                          <ExternalLink className="w-3 h-3" />
                        )}
                        {b.flyer_pdf_id ? "View attached PDF" : "Open flyer link"}
                      </a>
                    ) : (
                      <span className="italic">No flyer link</span>
                    )}
                    {b.expires_at && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        Expires {formatDate(b.expires_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setEditing(b)}
                    data-testid={`promo-edit-${b.id}`}
                    aria-label="Edit promo banner"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:bg-red-50 hover:text-destructive"
                    onClick={() => setConfirmDelete(b)}
                    data-testid={`promo-delete-${b.id}`}
                    aria-label="Delete promo banner"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <PromoBannerFormDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        manufacturerId={manufacturerId}
        banner={editing && editing !== "new" ? editing : null}
        onSaved={() => load()}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete promo banner?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{confirmDelete?.title}&rdquo; will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="promo-confirm-delete-btn"
            >
              {deleting && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
